import { zodResolver } from '@hookform/resolvers/zod';
import { useForm } from 'react-hook-form';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import { z } from 'zod';
import { authApi } from '@/api';
import { MODULE_HUB_PATH } from '@/constants/authRoutes';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { useState } from 'react';
import { showApiError } from '@/utils/apiError';
import { notify } from '@/core/toast/notify';
import { fattahProjectTitle } from '@/config/brand';

const schema = z
  .object({
    newPassword: z.string().min(8, 'Min 8 characters'),
    confirmPassword: z.string(),
  })
  .refine((d) => d.newPassword === d.confirmPassword, {
    message: 'Passwords must match',
    path: ['confirmPassword'],
  });

type FormData = z.infer<typeof schema>;

export function ResetPasswordPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get('token') ?? '';
  const [loading, setLoading] = useState(false);
  const { register, handleSubmit, formState: { errors } } = useForm<FormData>({
    resolver: zodResolver(schema),
  });

  const onSubmit = async (data: FormData) => {
    if (!token) {
      notify.error('Invalid reset link');
      return;
    }
    setLoading(true);
    try {
      await authApi.resetPassword({ token, newPassword: data.newPassword });
      notify.success('Password reset successful');
      navigate(MODULE_HUB_PATH);
    } catch (err) {
      showApiError(err, 'Reset failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="card p-8">
      <h2 className="text-2xl font-bold text-center">{fattahProjectTitle('Reset password')}</h2>
      <form onSubmit={handleSubmit(onSubmit)} className="mt-6 space-y-4">
        <Input
          label="New password"
          type="password"
          {...register('newPassword')}
          error={errors.newPassword?.message}
        />
        <Input
          label="Confirm password"
          type="password"
          {...register('confirmPassword')}
          error={errors.confirmPassword?.message}
        />
        <Button type="submit" className="w-full" loading={loading} disabled={!token}>
          Reset password
        </Button>
      </form>
      <Link to={MODULE_HUB_PATH} className="mt-4 inline-block text-sm text-primary-600 hover:underline">
        Back to login
      </Link>
    </div>
  );
}
